<script lang="ts">import { languageIsExpanded, menuAutoCollapsed } from "../state";
import { cfgCdnBase, cfgLanguageRedirects, cfgToThemeCdnPath, stateLanguageOptions, stateUi } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { clearSelectedLocale, loggedIn, selectedLanguageOption, selectedLocale } from "@infinitro/global";
import { DropdownArrowIcon, LocalIcon, RemoteIcon } from "@infinitro/icons";
import { onMount } from "svelte";
const CDN_BASE = cfgCdnBase();
let isMenuCollapsed = $state(false);
let dropdownOpen = $state(false);
const toggleDropdown = () => {
	dropdownOpen = !dropdownOpen;
	languageIsExpanded.set(dropdownOpen);
};
const redirects = cfgLanguageRedirects();
const selectLanguage = (language) => {
	if (redirects[language.code]) {
		const base = redirects[language.code];
		window.location.href = $loggedIn ? `${base}/player/` : `${base}/`;
		return;
	}
	// Unprefixed URL: updates UI + storage only; prefixed URL: also rewrites /xx/ (see `selectedLocale` in @infinitro/global).
	selectedLocale.set(language.code);
	dropdownOpen = false;
	languageIsExpanded.set(false);
};
menuAutoCollapsed.subscribe((value) => {
	dropdownOpen = false;
	isMenuCollapsed = value;
});
const handleClickOutside = (event) => {
	const eventTarget = event.target;
	if (!(eventTarget instanceof Element) || !eventTarget.closest(".custom-dropdown")) {
		dropdownOpen = false;
		languageIsExpanded.set(false);
	}
};
onMount(() => {
	document.addEventListener("click", handleClickOutside);
	return () => document.removeEventListener("click", handleClickOutside);
});
let useAlternative = $derived($stateUi.sidebar.sidebarStyle === "alternative");
let useAlternative2 = $derived($stateUi.sidebar.sidebarStyle === "alternative2");
let useAlternative4 = $derived($stateUi.sidebar.sidebarStyle === "alternative4");
let useDropdownBackgroundImage = $derived($stateUi.dropdown.useDropdownBackgroundImage);
let useRemoteIcons = $derived($stateUi.sidebar.useRemoteIcons ?? false);
</script>

{#if $stateLanguageOptions.length > 1}
	<!-- svelte-ignore a11y_no_static_element_interactions -->
	<div class="custom-dropdown {isMenuCollapsed && 'custom-dropdown-collapsed'}">
		<!-- svelte-ignore a11y_click_events_have_key_events -->
		<!-- svelte-ignore a11y_no_static_element_interactions -->
		<div
			class:dropdown-toggle-alternative={useAlternative || useAlternative4}
			class:dropdown-toggle-alternative-4={useAlternative4}
			class:dropdown-toggle-alternative-2={useAlternative2}
			class={`dropdown-toggle ${isMenuCollapsed ? 'dropdown-toggle-collapsed' : ''}`}
			onclick={toggleDropdown}
			ondblclick={() => clearSelectedLocale()}
		>
			<div class="d-flex {isMenuCollapsed && 'd-flex-collapsed'}">
				{#if $selectedLanguageOption}
					<img
						src={`${CDN_BASE}/images/flags/${$selectedLanguageOption.flag}.png`}
						alt={$t(Verbiage.icon_alt, 'alts')}
						class="flag-icon"
					/>
					{#if !isMenuCollapsed}
						<span class="toggle-text">{$selectedLanguageOption.label}</span>
					{/if}
				{:else}
					<span class="toggle-text">Select Language</span>
				{/if}
			</div>
			{#if useDropdownBackgroundImage}
				<div class="dropdown-background-image-container">
					<img
						src={cfgToThemeCdnPath('/button/background.png')}
						class="dropdown-background-image"
						alt={$t(Verbiage.illustration_alt, 'alts')}
					/>
				</div>
			{/if}
			{#if !isMenuCollapsed}
				<div
					class:arrow-container-alternative={useAlternative}
					class:arrow-container-alternative-2={useAlternative2}
					class:arrow-container-alternative-4={useAlternative4}
					class={`arrow-container ${dropdownOpen ? 'rotated' : ''}`}
				>
					{#if useAlternative4 && !useRemoteIcons}
						<LocalIcon icon="ArrowIcon" />
					{:else if useRemoteIcons}
						<RemoteIcon icon="ArrowIcon" size={22} />
					{:else}
						<DropdownArrowIcon />
					{/if}
				</div>
			{/if}
		</div>
		<div
			class:dropdown-menu-alternative={useAlternative || useAlternative4}
			class:dropdown-menu-alternative-4={useAlternative4}
			class:dropdown-menu-alternative-2={useAlternative2}
			class={`dropdown-menu ${isMenuCollapsed ? 'dropdown-menu-collapsed' : ''}`}
			class:expanded={dropdownOpen}
		>
			<div class="dropdown-menu-inner">
				{#each $stateLanguageOptions as language (language.code)}
					{#if redirects[language.code]}
						<!-- eslint-disable svelte/no-navigation-without-resolve -- cross-locale redirect URL -->
						<a
							href={$loggedIn
								? `${redirects[language.code]}/player/`
								: `${redirects[language.code]}/`}
							class:dropdown-item={true}
							class:selected={$selectedLanguageOption &&
								$selectedLanguageOption.code === language.code}
						>
							<img
								src={`${CDN_BASE}/images/flags/${language.flag}.png`}
								alt={$t(Verbiage.icon_alt, 'alts')}
								class="flag-icon"
							/>
							{#if !isMenuCollapsed}
								<span class="toggle-text">{language.label}</span>
							{/if}
						</a>
						<!-- eslint-enable svelte/no-navigation-without-resolve -->
					{:else}
						<!-- svelte-ignore a11y_click_events_have_key_events -->
						<!-- svelte-ignore a11y_no_static_element_interactions -->
						<div
							class:dropdown-item={true}
							class:selected={$selectedLanguageOption &&
								$selectedLanguageOption.code === language.code}
							onclick={() => selectLanguage(language)}
						>
							<img
								src={`${CDN_BASE}/images/flags/${language.flag}.png`}
								alt={$t(Verbiage.icon_alt, 'alts')}
								class="flag-icon"
							/>
							{#if !isMenuCollapsed}
								<span class="toggle-text">{language.label}</span>
							{/if}
						</div>
					{/if}
				{/each}
			</div>
		</div>
	</div>
{/if}

<style>
	.custom-dropdown {
		position: relative;
		z-index: 40;
		width: 100%;
}

	.custom-dropdown-collapsed {
		width: auto;
}

	.dropdown-toggle {
		position: relative;
		z-index: 20;
		display: block;
		height: 2.5rem;
		width: 100%;
		cursor: pointer;
		border-radius: 16px;
		border-width: 1px;
		border-color: var(--color-neutral-900);
		padding: 0.625rem;
		font-size: 0.75rem;
		line-height: 1rem;
		color: var(--color-text-100);
		display: flex;
		align-items: center;
		justify-content: space-between;
		background-image: var(--gradient-alternative-4);
}

	.dropdown-toggle-alternative {
		border-color: var(--color-accent-700);
		background-color: var(--color-background-500);
		border-radius: 12px;
		background-image: unset;
}

	.dropdown-toggle-alternative-4 {
		border-width: 0px;
		background-color: unset;
}

	.dropdown-toggle-alternative-2 {
		position: relative;
		border-radius: 8px;
		border-style: none;
		background-image: unset;
		background: radial-gradient(
			100% 100% at 0% 50%,
			var(--color-tertiary-300) 0%,
			var(--color-tertiary-500) 100%
		) !important;
}

	.dropdown-toggle-alternative-2::before {
		position: absolute;
		inset: 0px;
		border-radius: 8px;
		padding: 1px;
		content: '';
		background: linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, transparent 100%);
		-webkit-mask:
			linear-gradient(#fff 0 0) content-box,
			linear-gradient(#fff 0 0);
		-webkit-mask-composite: xor;
		mask-composite: exclude;
		pointer-events: none;
		z-index: 2;
}

	.dropdown-toggle-collapsed {
		width: 100%;
}

	.dropdown-menu {
		position: absolute;
		z-index: 10;
		margin-top: -31px;
		width: 100%;
		border-radius: 16px;
		border-width: 1px;
		border-color: var(--color-neutral-900);
		padding-top: 28px;
		background: var(--gradient-alternative-4);
		overflow: hidden;
		transition:
			max-height 320ms cubic-bezier(0.22, 1, 0.36, 1),
			opacity 260ms ease,
			transform 320ms cubic-bezier(0.22, 1, 0.36, 1);
		max-height: 0 !important;
		opacity: 0;
		transform: translateY(6px);
		pointer-events: none;
		will-change: max-height, opacity, transform;
}

	.dropdown-menu.expanded {
		max-height: 450px !important;
		opacity: 1;
		transform: translateY(0);
		pointer-events: auto;
	}

	.dropdown-menu-inner {
		width: 100%;
		overflow-y: auto;
		max-height: 422px;
}

	.dropdown-menu-inner::-webkit-scrollbar {
		width: 0.25rem;
}

	.dropdown-menu-inner::-webkit-scrollbar-track {
		border-radius: 0.5rem;
		background-color: var(--color-background-500);
		padding: 0.25rem;
}

	.dropdown-menu-inner::-webkit-scrollbar-thumb {
		border-radius: 0.5rem;
		background-color: var(--color-neutral-900);
		--tw-shadow-color: var(--shadow-neutral);
		--tw-shadow: var(--tw-shadow-colored);
}

	.dropdown-menu-inner::-webkit-scrollbar-thumb:hover {
		background-color: var(--color-neutral-500);
}

	.dropdown-menu-alternative {
		border-color: var(--color-accent-700);
		background-color: var(--color-background-500);
		border-radius: 12px;
		background-image: unset;
}

	.dropdown-menu-alternative-4 {
		border-width: 0px;
		background-color: unset;
}

	.dropdown-menu-alternative-2 {
		margin-top: -4px;
		border-radius: 8px;
		border-top-left-radius: 0px;
		border-top-right-radius: 0px;
		border-top-width: 0px;
		padding-top: 4px;
		background-image: unset;
}

	.dropdown-menu-collapsed {
		width: 100%;
}

	.dropdown-item {
		cursor: pointer;
		padding: 0.625rem;
		font-size: 0.75rem;
		line-height: 1rem;
		--tw-text-opacity: 1;
		color: rgb(156 163 175 / var(--tw-text-opacity, 1));
		display: flex;
		align-items: center;
		gap: 0.5rem;
}

	.dropdown-item-collapsed {
		justify-content: center;
		padding-left: 0px;
		padding-right: 0px;
		padding-top: 0.375rem;
		padding-bottom: 0.375rem;
}

	.dropdown-item:hover {
		color: var(--color-text-100);
}

	.dropdown-item.selected {
		color: var(--color-text-100);
}

	.flag-icon {
		height: auto;
		width: 23px;
}

	.toggle-text {
		font-size: 14px;
		opacity: 1;
		transition-property: opacity;
		transition-duration: 300ms;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		font-family: var(--font-main);
}

	.arrow-container {
		display: flex;
		height: 1rem;
		width: 1rem;
		align-items: center;
		justify-content: center;
		transition: transform 320ms cubic-bezier(0.22, 1, 0.36, 1);
}

	.arrow-container-alternative-2 {
		background-color: transparent;
		background-image: unset !important;
}

	.arrow-container-alternative {
		border-width: 0px;
		background-color: var(--color-background-100);
		width: unset;
		height: unset;
		background-image: unset !important;
}

	.arrow-container-alternative-4 {
		background-color: transparent;
		background-image: unset !important;
}

	.arrow-container.rotated {
		transform: rotate(180deg);
	}

	.d-flex {
		display: flex;
		align-items: center;
		gap: 0.5rem;
}

	.dropdown-background-image-container {
		position: absolute;
		inset: 0px;
		height: 100%;
		width: 100%;
		overflow: hidden;
		border-radius: inherit;
}

	.dropdown-background-image {
		position: absolute;
		top: 0px;
		max-width: 100px;
		opacity: 0.2;
		left: 80%;
		transform: translateX(-50%) translateY(-50%);
		width: 100%;
		height: auto;
		-o-object-fit: cover;
		   object-fit: cover;
}
</style>
